/**
 * Reliability primitives for the imaging pipeline.
 *
 * Rule for every stage of the seller capture flow: it either succeeds, or it
 * fails with a message — it must never hang. Each helper here is deliberately
 * tiny and dependency-free so it can run inside a Web Worker too.
 */

export class StageTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${Math.round(ms / 1000)}s`);
    this.name = "StageTimeoutError";
  }
}

/** Reject if `work` has not settled within `ms`. */
export function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new StageTimeoutError(label, ms)), ms);
    work.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

export type StageOptions = {
  /** Hard limit for a single attempt. */
  timeoutMs?: number;
  /** Retries after the first failure. Defaults to one automatic retry. */
  retries?: number;
  /** Delay before the retry. */
  retryDelayMs?: number;
  onRetry?: (error: unknown, attempt: number) => void;
};

/**
 * Run a stage with a hard timeout and one automatic retry. Rejects with the
 * last error so the UI can show something useful rather than spinning.
 */
export async function runStage<T>(
  label: string,
  work: () => Promise<T>,
  options: StageOptions = {},
): Promise<T> {
  const { timeoutMs = 60_000, retries = 1, retryDelayMs = 400, onRetry } = options;
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await withTimeout(work(), timeoutMs, label);
    } catch (err) {
      lastError = err;
      if (attempt === retries) break;
      onRetry?.(err, attempt + 1);
      if (retryDelayMs > 0) await new Promise((r) => setTimeout(r, retryDelayMs));
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`${label} failed`);
}

/** Never-throwing variant: returns `null` instead of blocking the flow. */
export async function runStageSoft<T>(
  label: string,
  work: () => Promise<T>,
  options: StageOptions = {},
): Promise<T | null> {
  try {
    return await runStage(label, work, options);
  } catch {
    return null;
  }
}

export function errorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string" && err) return err;
  return fallback;
}
